import { useEffect, useRef } from "react";
import { useRouter } from "expo-router";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import {
  getAuth,
  PhoneMultiFactorInfo,
  type RecaptchaVerifier,
} from "firebase/auth";
import { useAtom, useAtomValue } from "jotai/react";
import { useForm } from "react-hook-form";

import { ScreenLayout } from "@/blocks/ScreenLayout";
import { VnBox } from "@/components/VnBox";
import { VnButton } from "@/components/VnButton/VnButton";
import { VnCard } from "@/components/VnCard";
import { VnForm } from "@/components/VnForm";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { VnOTPInput } from "@/components/VnOTPInput/VnOTPInput";
import { VnRecaptcha } from "@/components/VnRecaptcha";
import { VnText } from "@/components/VnText/VnText";
import { PatientsApi } from "@/data/api/patientsApi";
import {
  useEnrollMfaMutation,
  useSendMfaVerificationCodeMutation,
  useVerifyMfaSignInMutation,
} from "@/data/mutations/authMutations";
import { PatientsQueryKeys } from "@/data/queries/patientsQueries";
import {
  MFA_OTP_LENGTH,
  mfaVerifyFormSchema,
  MfaVerifyFormType,
} from "@/domain/auth";
import { useTranslator } from "@/hooks/useTranslate";
import { createRecaptchaVerifier } from "@/services/firebase";
import {
  mfaResolverAtom,
  mfaVerificationIdAtom,
  onboardingShortCodeDataAtom,
} from "@/stores/atoms";
import { logger } from "@/utils/logger";

const MfaVerifyScreen = () => {
  const { t } = useTranslator();
  const router = useRouter();
  const auth = getAuth();
  const recaptchaVerifier = useRef<RecaptchaVerifier | null>(null);

  useEffect(() => {
    recaptchaVerifier.current ??= createRecaptchaVerifier(
      "recaptcha-container",
    );

    return () => {
      recaptchaVerifier.current?.clear();
      recaptchaVerifier.current = null;
    };
  }, [auth]);

  const resolver = useAtomValue(mfaResolverAtom);
  const [verificationId, setVerificationId] = useAtom(mfaVerificationIdAtom);
  const onboardingShortCodeData = useAtomValue(onboardingShortCodeDataAtom);

  const hasTriggeredSend = useRef(false);

  // Enrolment must finish before the API will return the patient.
  const queryClient = useQueryClient();

  const { mutate: enrollMfa, isPending: isEnrolling } = useEnrollMfaMutation(
    onboardingShortCodeData?.code ?? "",
  );
  const { mutate: verifyMfaSignIn, isPending: isVerifying } =
    useVerifyMfaSignInMutation(onboardingShortCodeData?.code ?? "");
  const { mutate: sendVerificationCode, isPending: isSendingCode } =
    useSendMfaVerificationCodeMutation();

  const isSubmittingCode = isEnrolling || isVerifying;

  const verifyForm = useForm<MfaVerifyFormType>({
    defaultValues: { code: "" },
    resolver: standardSchemaResolver(mfaVerifyFormSchema),
  });

  const isCodeValid = verifyForm.formState.isValid;

  // Auto-send verification code if we are in sign-in flow and haven't sent it yet
  useEffect(() => {
    if (resolver && !verificationId && !hasTriggeredSend.current) {
      hasTriggeredSend.current = true;
      handleSendCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolver, verificationId]);

  const handleSendCode = () => {
    if (resolver) {
      if (!recaptchaVerifier.current) return;

      sendVerificationCode(
        {
          phoneNumber: "", // Will use resolver hint under the hood
          applicationVerifier: recaptchaVerifier.current,
          resolver,
        },
        {
          onSuccess: newVerificationId => {
            if (newVerificationId) {
              setVerificationId(newVerificationId);
            }
          },
          onError: error => {
            logger.error({
              message: "Failed to send MFA verification code during sign in",
              error,
            });
          },
        },
      );
    } else {
      // In enrollment flow, we don't have the phone number here.
      // Redirecting back will let them re-enter and get a new code.
      router.back();
    }
  };

  const handleSubmit = (data: MfaVerifyFormType) => {
    if (!verificationId) return;

    const onSuccess = () => {
      if (resolver) {
        router.replace("/(authed)");
        return;
      }

      void queryClient
        .fetchQuery({
          queryKey: PatientsQueryKeys.me(),
          queryFn: () => PatientsApi.getCurrentPatient(),
        })
        .then(patient => {
          if (!patient?.hasCompletedProfile) {
            router.replace("/(authed)/registration/profile");
          } else if (!patient.hasSignedConsentDocument) {
            router.replace("/(authed)/registration/consent-form");
          } else {
            router.replace("/(authed)");
          }
        });
    };

    const onError = (error: unknown) => {
      logger.error({
        message: "Failed to verify MFA OTP",
        error,
      });
      verifyForm.setError("code", {
        message: t("common:validation.invalidCode"),
      });
    };

    if (resolver) {
      verifyMfaSignIn(
        {
          resolver,
          verificationId,
          code: data.code,
        },
        { onSuccess, onError },
      );
    } else {
      enrollMfa(
        {
          verificationId,
          code: data.code,
        },
        { onSuccess, onError },
      );
    }
  };

  const displayPhoneNumber = (
    resolver?.hints[0] as PhoneMultiFactorInfo | undefined
  )?.phoneNumber;

  return (
    <ScreenLayout className="py-12">
      <VnBox className="mt-safe-offset-10 web:mt-10 flex h-full items-center px-4 md:mt-0 md:justify-center">
        <VnBox className="w-full gap-4 md:w-3/6">
          <VnBox className="flex flex-col gap-6">
            <VnBox className="gap-2">
              <VnText
                variant="headerOne"
                className="text-center"
                i18nKey="native:mfaVerifyScreen.title"
              />
              <VnText
                variant="lg"
                className="text-muted-foreground text-center"
                i18nKey="native:mfaVerifyScreen.subtitle"
                i18nOptions={{ phoneNumber: displayPhoneNumber }}
              />
            </VnBox>
            <VnForm form={verifyForm} isInputOnlyForm>
              <VnCard className="gap-6 rounded-2xl p-6">
                <VnOTPInput
                  control={verifyForm.control}
                  labelI18nKey="native:mfaVerifyScreen.verificationCodeLabel"
                  name="code"
                  otpLength={MFA_OTP_LENGTH}
                  onComplete={() => {
                    void verifyForm.handleSubmit(handleSubmit)();
                  }}
                  isRequired
                  isDisabled={isSubmittingCode || isSendingCode}
                />
                <VnBox className="items-center">
                  {isSendingCode ? (
                    <VnLoadingSpinner />
                  ) : (
                    <VnText
                      variant="sm"
                      className="text-muted-foreground -mt-3 cursor-pointer underline"
                      onPress={() => {
                        void handleSendCode();
                      }}
                      i18nKey="native:mfaVerifyScreen.getNewCode"
                    />
                  )}
                </VnBox>
                <VnButton
                  onPress={() => {
                    void verifyForm.handleSubmit(handleSubmit)();
                  }}
                  i18nKey="common:continue"
                  isLoading={isSubmittingCode}
                  className="rounded-xl"
                  disabled={!isCodeValid}
                />
              </VnCard>
            </VnForm>
          </VnBox>
        </VnBox>
      </VnBox>
      <VnRecaptcha />
    </ScreenLayout>
  );
};

export { MfaVerifyScreen };
